import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { LostItem } from "@/types";
import { Info, Signature, Undo, Trash, Edit } from "lucide-react";

interface LostItemTimelineModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: LostItem | null;
}

const statusSteps = [
  { status: "Pending", label: "Item registered" },
  { status: "Delivered", label: "Delivered to guest" },
  { status: "Discarded", label: "Discarded" },
  { status: "Deleted", label: "Deleted (pending erasure)" },
];

const getStepLabel = (status: string) =>
  statusSteps.find((s) => s.status === status)?.label || status;
const getStepIcon = (status: string) => {
  switch (status) {
    case "Pending":
      return <Edit className="text-purple-500" />;
    case "Delivered":
      return <Signature className="text-green-500" />;
    case "Discarded":
      return <Undo className="text-orange-500" />;
    case "Deleted":
      return <Trash className="text-gray-400" />;
    default:
      return <Info />;
  }
};

const LostItemTimelineModal = ({
  open,
  onOpenChange,
  item,
}: LostItemTimelineModalProps) => {
  if (!item) return null;
  // History simulation: pending, delivered (w signature), discarded, deleted
  const steps = [
    {
      date: item.date,
      status: "Pending",
      receiver: undefined,
    },
  ];
  if (item.status === "Delivered" && item.receiver_name) {
    steps.unshift({
      date: "Just now",
      status: "Delivered",
      receiver: item.receiver_name,
    });
  }
  if (item.status === "Discarded") {
    steps.unshift({
      date: "Just now",
      status: "Discarded",
      receiver: undefined,
    });
  }
  if (item.status === "Deleted") {
    steps.unshift({
      date: "Just now",
      status: "Deleted",
      receiver: undefined,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xs p-4 rounded-xl shadow-lg">
        <DialogHeader className="pb-0">
          <DialogTitle className="flex items-center gap-2">
            <Signature className="h-5 w-5 text-purple-500" />
            Item Timeline
          </DialogTitle>
          <DialogClose className="absolute right-4 top-4 rounded-full opacity-70 hover:bg-muted hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring p-1 transition-all">
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <div className="py-3">
          <ol className="relative border-l border-gray-200 dark:border-gray-700">
            {steps.map((s, i) => (
              <li key={i} className="mb-7 ml-6 relative">
                <span className="absolute -left-3 flex items-center justify-center w-8 h-8 rounded-full bg-white shadow">
                  {getStepIcon(s.status)}
                </span>
                <h3 className="flex items-center mb-1 text-base font-semibold text-gray-900 dark:text-white">
                  {getStepLabel(s.status)}
                </h3>
                <time className="block mb-1 text-xs font-normal leading-none text-gray-400 dark:text-gray-500">
                  {s.date}
                </time>
                {s.receiver && (
                  <div className="text-xs text-slate-700 dark:text-slate-300 mb-1">
                    Receiver:{" "}
                    <span className="font-semibold">{s.receiver}</span>
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default LostItemTimelineModal;
